import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Spinner, Modal, ModalHeader, ModalBody, Button, Input, Label, FormFeedback } from 'reactstrap';
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//Import Breadcrumb
import BreadCrumb from "../../../Components/Common/BreadCrumb";
//redux
import { Link } from "react-router-dom";
import { 
  getSubcategories, 
  addNewSubcategory, 
  updateSubcategory as updateSubcategoryThunk, 
  deleteSubcategory,
  getEcommerceCategories,
  getProducts 
} from "../../../slices/ecommerce/thunk";
import { getUserRole } from "../../../helpers/role_helper";
import { getLoggedinUser } from "../../../api";
import DeleteModal from "../../../Components/Common/DeleteModal";

const EcommerceSubCategory = (props) => {
  document.title = "Product Subcategory - Dashboard";
  const dispatch = useDispatch();

  // Get user info for filtering
  const userProfile = getLoggedinUser();
  const userRole = getUserRole(userProfile);
  const userId = userProfile?.id || userProfile?.uid || userProfile?._id;

  // Modal states
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [subcategoryData, setSubcategoryData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  // Redux selectors
  const ecommerceData = createSelector(
    (state) => state.Ecommerce,
    (ecommerce) => ({
      subcategories: ecommerce.subcategories || [],
      categories: ecommerce.categories || [],
      products: ecommerce.products || [],
      loading: ecommerce.loading || false,
      error: ecommerce.error
    })
  );
  const { subcategories, categories, products, loading, error } = useSelector(ecommerceData);

  // Fetch subcategories and categories on component mount (common only - no seller filter)
  useEffect(() => {
    dispatch(getSubcategories({}));
    dispatch(getEcommerceCategories({}));
    dispatch(getProducts());
  }, [dispatch]);

  // Validation schema
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: subcategoryData?.name || "",
      category_id: subcategoryData?.category_id || subcategoryData?.categoryId || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Subcategory name is required"),
      category_id: Yup.string().required("Category is required"),
    }),
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          const updateSubcategory = {
            id: subcategoryData.id,
            name: values.name,
            category_id: values.category_id,
            seller_id: null,
          };
          await dispatch(updateSubcategoryThunk(updateSubcategory)).unwrap();
        } else {
          const newSubcategory = {
            name: values.name,
            category_id: values.category_id,
            seller_id: null,
            status: "active",
          };
          await dispatch(addNewSubcategory(newSubcategory)).unwrap();
        }
        // Close modal and reset form only after successful submission
        toggle();
        validation.resetForm();
      } catch (error) {
        // Error toast is already handled in the thunk
        // Don't close modal on error so user can fix and retry
        console.error("Subcategory operation failed:", error);
      }
    },
  });

  // Toggle modal
  const toggle = () => {
    if (modal) {
      setModal(false);
      setSubcategoryData(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  // Handle edit
  const handleEditClick = (subcategory) => {
    setSubcategoryData(subcategory);
    setIsEdit(true);
    setModal(true);
    validation.setFieldValue("name", subcategory.name || subcategory.subcategory);
    validation.setFieldValue("category_id", subcategory.category_id || subcategory.categoryId || "");
  };

  // Handle delete click
  const handleDeleteClick = (subcategory) => {
    setDeleteId(subcategory.id);
    setDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteSubcategory = () => {
    // Check if any products exist under this subcategory
    const productsInSubcategory = products.filter(
      (product) => product.subcategory_id === deleteId || product.subcategoryId === deleteId
    );

    if (productsInSubcategory.length > 0) {
      alert(`Cannot delete subcategory. There are ${productsInSubcategory.length} product(s) associated with this subcategory. Please remove or reassign those products first.`);
      setDeleteModal(false);
      return;
    }

    dispatch(deleteSubcategory(deleteId));
    setDeleteModal(false);
    setDeleteId(null);
  };

  // Map database subcategories to display format
  const subcategoryList = subcategories.map((subcat) => {
    // Find category name - convert both IDs to strings for reliable comparison
    const subcatCategoryId = String(subcat.category_id || subcat.categoryId || '');
    const category = categories.find(
      (cat) => {
        const catId = String(cat.id || cat._id || '');
        return catId === subcatCategoryId && catId !== '';
      }
    );
    
    return {
      id: subcat.id || subcat._id,
      name: subcat.name,
      subcategory: subcat.name,
      category: category?.name || "N/A",
      category_id: subcat.category_id || subcat.categoryId,
      status: subcat.status === 'active' ? 'Approved' : subcat.status === 'inactive' ? 'Rejected' : 'Pending',
      slug: subcat.slug,
      description: subcat.description,
      seller_id: subcat.seller_id
    };
  });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Product Subcategory" pageTitle="Seller" />
        <Row>
          <Card>
            <CardBody>
              <div className="listjs-table" id="customerList">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Product Subcategory</h5>
                  </div>
                  <div className="col-sm-auto">
                    {userRole !== "seller" && (
                    <div className="d-flex gap-1 flex-wrap">
                      <button 
                        type="button" 
                        className="btn btn-info"
                        onClick={toggle}
                      >
                        <i className="ri-add-circle-line align-bottom me-1"></i>{" "}
                        Add Subcategory
                      </button>
                    </div>
                    )}
                  </div>
                </Row>

                <div className="table-responsive table-card mt-3 mb-1">
                  <table className="table align-middle table-nowrap" id="customerTable">
                    <thead className="table-light">
                      <tr>
                        <th scope="col" style={{ width: "50px" }}>
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="checkAll" value="option" />
                          </div>
                        </th>
                        <th className="sort" data-sort="id">Id</th>
                        <th className="sort" data-sort="category">Category</th>
                        <th className="sort" data-sort="subcategory">Subcategory</th>
                        <th className="sort" data-sort="status">Status</th>
                        {userRole !== "seller" && <th className="sort" data-sort="action">Operations</th>}
                      </tr>
                    </thead>
                    <tbody className="list form-check-all">
                      {loading ? (
                        <tr>
                          <td colSpan={userRole === "seller" ? 5 : 6} className="text-center">
                            <Spinner className="me-2" /> Loading subcategories...
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan={userRole === "seller" ? 5 : 6} className="text-center text-danger">
                            Error loading subcategories: {typeof error === 'string' ? error : (error?.message || JSON.stringify(error) || 'Unknown error')}
                          </td>
                        </tr>
                      ) : subcategoryList.length === 0 ? (
                        <tr>
                          <td colSpan={userRole === "seller" ? 5 : 6} className="text-center">
                            {userRole === "seller" ? "No subcategories found." : "No subcategories found. Add your first subcategory!"}
                          </td>
                        </tr>
                      ) : (
                        subcategoryList.map((item, index) => (
                        <tr key={item.id}>

                          {/* Checkbox */}
                          <th scope="row">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                name="checkAll"
                                value={item.id}
                              />
                            </div>
                          </th>

                          {/* ID */}
                          <td className="id">{item.id}</td>

                          {/* Category */}
                          <td className="category">{item.category}</td>

                          {/* Subcategory */}
                          <td className="subcategory">{item.subcategory}</td>

                          <td className="status">
                            <span
                              className={`badge ${item.status === "Approved"
                                ? "bg-success"
                                : item.status === "Rejected"
                                  ? "bg-danger"
                                  : "bg-warning"
                                }`}
                            >
                              {item.status}
                            </span>
                          </td>

                          {/* Operations - admin only */}
                          {userRole !== "seller" && (
                          <td>
                            <div className="d-flex gap-2">
                              <div className="edit">
                                <button
                                  className="btn btn-sm btn-success edit-item-btn"
                                  onClick={() => handleEditClick(item)}
                                >
                                  <i className="ri-edit-line"></i>
                                </button>
                              </div>

                              <div className="remove">
                                <button
                                  className="btn btn-sm btn-danger remove-item-btn"
                                  onClick={() => handleDeleteClick(item)}
                                >
                                  <i className="ri-delete-bin-2-line"></i>
                                </button>
                              </div>
                            </div>
                          </td>
                          )}

                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  <div className="noresult" style={{ display: "none" }}>
                    <div className="text-center">
                      <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop"
                        colors="primary:#121331,secondary:#08a88a" style={{ width: "75px", height: "75px" }}>
                      </lord-icon>
                      <h5 className="mt-2">Sorry! No Result Found</h5>
                      <p className="text-muted mb-0">We've searched more than 150+ Orders We did not find any
                        orders for you search.</p>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <div className="pagination-wrap hstack gap-2">
                    <Link className="page-item pagination-prev disabled" to="#">
                      Previous
                    </Link>
                    <ul className="pagination listjs-pagination mb-0"></ul>
                    <Link className="page-item pagination-next" to="#">
                      Next
                    </Link>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Row>
      </Container>

      {/* Add/Edit Subcategory Modal */}
      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          {isEdit ? "Edit Subcategory" : "Add Subcategory"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}>
            <div className="mb-3">
              <Label htmlFor="category_id" className="form-label">
                Category <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                className="form-control"
                id="category_id"
                name="category_id"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.category_id || ""}
                invalid={
                  validation.touched.category_id && validation.errors.category_id ? true : false
                }
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id || category._id} value={category.id || category._id}>
                    {category.name}
                  </option>
                ))}
              </Input>
              {validation.touched.category_id && validation.errors.category_id ? (
                <FormFeedback type="invalid">
                  {validation.errors.category_id}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="name" className="form-label">
                Subcategory Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                className="form-control"
                id="name"
                name="name"
                placeholder="Enter subcategory name"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.name || ""}
                invalid={
                  validation.touched.name && validation.errors.name ? true : false
                }
              />
              {validation.touched.name && validation.errors.name ? (
                <FormFeedback type="invalid">
                  {validation.errors.name}
                </FormFeedback>
              ) : null}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" color="light" onClick={toggle}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                {isEdit ? "Update" : "Add"} Subcategory
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteSubcategory}
        onCloseClick={() => {
          setDeleteModal(false);
          setDeleteId(null);
        }}
      />

      {/* Toast Container */}
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default EcommerceSubCategory;
